
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LoadingController, AlertController, ToastController, NavController } from '@ionic/angular';
import { Router } from '@angular/router';
@Injectable({
  providedIn: 'root'
})
export class UtilService {
  loader: any;
  isLoading = false;

  details: any;

  constructor(
    public loadingCtrl: LoadingController,
    public alertCtrl: AlertController,
    private toastCtrl: ToastController,
    public router: Router,
    private navCtrl: NavController,
    private dom: DomSanitizer,
  ) {
  }

  formatKeteranganHtml(value: any): SafeHtml {
    if (value === null || value === undefined || String(value).trim() === '') {
      return this.dom.bypassSecurityTrustHtml('-');
    }

    const escaped = String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const withBreaks = escaped.replace(/&lt;br\s*\/?&gt;/gi, '<br>');
    return this.dom.bypassSecurityTrustHtml(withBreaks);
  }

  async show(msg?, cssClass: string = '') {
    this.isLoading = true;
    return await this.loadingCtrl.create({
      message: msg,
      spinner: 'bubbles',
      cssClass: cssClass,
    }).then(a => {
      a.present().then(() => {
        //console.log('presented');
        if (!this.isLoading) {
          a.dismiss().then(() => console.log('abort presenting'));
        }
      });
    });
  }

  async hide() {
    this.isLoading = false;
    return await this.loadingCtrl.dismiss().then(() => console.log('dismissed'));
  }

  /*
    Show Warning Alert Message
    param : msg = message to display
    Call this method to show Warning Alert,
    */
  async showWarningAlert(msg) {
    const alert = await this.alertCtrl.create({
      header: 'Warning',
      message: msg,
      buttons: ['OK']
    });

    await alert.present();
  }

  async showSimpleAlert(msg) {
    const alert = await this.alertCtrl.create({
      header: '',
      message: msg,
      buttons: ['OK']
    });

    await alert.present();
  }

  /*
   Show Error Alert Message
   param : msg = message to display
   Call this method to show Error Alert,
   */
  async showErrorAlert(msg) {
    const alert = await this.alertCtrl.create({
      header: 'Error',
      message: msg,
      buttons: ['OK']
    });

    await alert.present();
  }

  /*
     param : email = email to verify
     Call this method to get verify email
     */
  async getEmailFilter(email) {
    var emailfilter = /^[\w._-]+[+]?[\w._-]+@[\w.-]+\.[a-zA-Z]{2,6}$/;
    if (!(emailfilter.test(email))) {
      const alert = await this.alertCtrl.create({
        header: 'Warning',
        message: 'Please enter valid email',
        buttons: ['OK']
      });
      await alert.present();
      return false;
    } else {
      return true;
    }
  }


  /*
    Show Toast Message on Screen
     param : msg = message to display, color= background color of toast example dark,danger,light. position  = position of message example top,bottom
     Call this method to show toast message
     */

  async showToast(msg, color, positon) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 2000,
      color: color || 'dark',
      position: positon || 'bottom'
    });
    toast.present();
  }

  normalizeNumberInput(event: any): string {
    const rawValue = event?.target?.value ?? '';
    return rawValue.toString().replace(/\D+/g, '');
  }

  normalizeNumberValue(value: any): string {
    const rawValue = value ?? '';
    return rawValue.toString().replace(/\D+/g, '');
  }

  isNumericOnly(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    const normalized = String(value).trim();
    return /^[0-9]+$/.test(normalized);
  }

  isNumericOnlyList(values: any[]): boolean {
    return values.every((value) => this.isNumericOnly(value));
  }

  normalizeDecimalInput(event: any): string {
    const rawValue =
      typeof event === 'string' || typeof event === 'number'
        ? event
        : event?.detail?.value ?? event?.target?.value ?? '';
    let normalized = rawValue.toString().replace(/,/g, '.').replace(/[^0-9.]/g, '');
    const firstDotIndex = normalized.indexOf('.');
    if (firstDotIndex !== -1) {
      const beforeDot = normalized.slice(0, firstDotIndex + 1);
      const afterDot = normalized.slice(firstDotIndex + 1).replace(/\./g, '');
      normalized = `${beforeDot}${afterDot}`;
    }
    return normalized;
  }

  formatIntegerForDisplay(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (isNaN(n)) return '';
    return new Intl.NumberFormat('id-ID').format(n);
  }

  formatDecimalForDisplay(normalized: string): string {
    if (!normalized) return '';
    const parts = normalized.toString().split('.');
    const intPart = parts[0] || '0';
    const decPart = parts[1] || '';
    const intFormatted = this.formatIntegerForDisplay(Number(intPart));
    return decPart ? `${intFormatted}.${decPart}` : intFormatted;
  }

  toNumber(value: any, fallback: number = 0): number {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  allowDecimalKey(event: KeyboardEvent): void {
    const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (allowedControlKeys.includes(event.key)) return;

    const isNumber = /^[0-9]$/.test(event.key);
    const isDecimalSep = event.key === '.' || event.key === ',';

    if (!isNumber && !isDecimalSep) {
      event.preventDefault();
    }
  }

  normalizeNominalValue(value: any): number {
    if (value === null || value === undefined) {
      return 0;
    }

    const normalized = String(value)
      .trim()
      .replace(/\./g, '')
      .replace(/,/g, '.')
      .replace(/[^0-9.-]/g, '');

    if (normalized === '' || normalized === '-' || normalized === '.') {
      return 0;
    }

    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) {
      return 0;
    }

    return parsed;
  }

  normalizeMongoDate(createdAt: any): number | null {
    if (!createdAt) {
      return null;
    }

    if (typeof createdAt === 'number') {
      return createdAt;
    }

    const numberLong = createdAt?.$date?.$numberLong;
    if (numberLong !== undefined && numberLong !== null && numberLong !== '') {
      const timestamp = Number(numberLong);
      return Number.isNaN(timestamp) ? null : timestamp;
    }

    if (createdAt?.date) {
      const timestamp = new Date(createdAt.date).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    }

    const parsed = new Date(createdAt).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }

  normalizePageInfo(info: any, fallbackCurrentPage: number = 1): any {
    const current = Number(info?.current_page) || Number(fallbackCurrentPage) || 1;
    const total = Number(info?.total_pages) || 1;

    const prevExplicit = info?.prev_page;
    const nextExplicit = info?.next_page;
    const lastExplicit = info?.last_page;

    const prev =
      prevExplicit === true || prevExplicit === 1 || prevExplicit === '1'
        ? true
        : prevExplicit === false || prevExplicit === 0 || prevExplicit === '0'
          ? false
          : current > 1;

    const next =
      nextExplicit === true || nextExplicit === 1 || nextExplicit === '1'
        ? true
        : nextExplicit === false || nextExplicit === 0 || nextExplicit === '0'
          ? false
          : current < total;

    const last =
      lastExplicit === true || lastExplicit === 1 || lastExplicit === '1'
        ? true
        : lastExplicit === false || lastExplicit === 0 || lastExplicit === '0'
          ? false
          : current < total;

    return {
      ...info,
      current_page: current,
      total_pages: total,
      prev_page: prev,
      next_page: next,
      last_page: last,
    };
  }

  async shoNotification(msg, color, positon) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 4000,
      color: color,
      position: positon,
      buttons: [
        {
          text: 'Ok',
          role: 'cancel',
          handler: () => {
            //console.log('Cancel clicked');
          }
        }
      ]
    });
    toast.present();
  }

  async errorToast(msg) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 2000,
    });
    toast.present();
  }

  apiErrorHandler(err) {
    //console.log("Error got in service =>", err)
    if (err.status == -1) {
      this.showErrorAlert('Failed To Connect With Server');
    } else if (err.status == 401) {
      this.showErrorAlert('Unauthorized Request!');
      this.navCtrl.navigateRoot('/login');
    } else if (err.status == 500) {
      this.showErrorAlert("Somethimg Went Wrong..");
    }
  }

  // setDetails(data) {
  //   this.details = null;
  //   this.details = data;
  // }
  // getDetails() {
  //   return this.details;
  // }

  makeid(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }
}